/**
 * ContinueLearningCard — dashboard widget listing the current user's
 * in-progress courses with mini progress bars and a Resume CTA.
 *
 * Data source: `GET /api/lms/my-progress` (already implemented in server.py)
 * which returns `{ progress: [{ course_id, course_title, percent_complete,
 * completed_lessons, total_lessons, last_lesson_id }] }`.
 *
 * Behaviour:
 *  - Silent no-op when the user has no in-progress courses.
 *  - Shows up to 3 courses; a "See all" link routes to /learning.
 *  - "Resume" jumps into the next un-completed lesson if available; otherwise
 *    opens the course detail page.
 */
import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { PlayCircle, GraduationCap, ArrowUpRight } from 'lucide-react';
import { AuthContext } from '../App';
import { Button } from './ui/button';
import { CompletedPill, ProgressBar } from './lms/CompletionBadges';

const ContinueLearningCard = () => {
  const { API } = useContext(AuthContext);
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/lms/my-progress`)
      .then((res) => {
        if (cancelled) return;
        const list = res.data?.progress || res.data?.items || [];
        // Only surface courses the user has actually started (or finished).
        const started = list.filter((c) => {
          const done = c.completed_lessons ?? c.lessons_completed ?? 0;
          return done > 0;
        });
        // In-progress first, then recently completed.
        const inProgress = started.filter((c) => (c.percent_complete ?? c.progress_percentage ?? 0) < 100);
        const completed  = started.filter((c) => (c.percent_complete ?? c.progress_percentage ?? 0) === 100);
        setItems([...inProgress, ...completed].slice(0, 3));
      })
      .catch(() => { /* silent */ })
      .finally(() => { if (!cancelled) setLoaded(true); });
    return () => { cancelled = true; };
  }, [API]);

  if (!loaded || items.length === 0) return null;

  const resume = (row) => {
    const lessonId = row.next_lesson_id || row.last_lesson_id;
    if (lessonId) navigate(`/lms/lesson/${lessonId}`);
    else navigate(`/course/${row.course_id}`);
  };

  return (
    <div className="pro-card p-5" data-testid="continue-learning-card">
      <div className="flex items-center justify-between mb-4">
        <div>
          <span className="eyebrow inline-flex items-center gap-1.5">
            <GraduationCap className="w-3.5 h-3.5" /> Continue learning
          </span>
          <h3 className="font-heading text-lg font-bold mt-1">
            {items.some((c) => (c.percent_complete ?? c.progress_percentage ?? 0) < 100)
              ? 'Pick up where you left off'
              : 'Take a victory lap'}
          </h3>
        </div>
        <button
          type="button"
          onClick={() => navigate('/learning')}
          className="text-xs font-semibold text-primary hover:underline inline-flex items-center gap-1"
          data-testid="continue-learning-see-all"
        >
          See all <ArrowUpRight className="w-3 h-3" />
        </button>
      </div>

      <ul className="space-y-3">
        {items.map((row) => {
          const pct = Math.round(row.percent_complete ?? row.progress_percentage ?? 0);
          const done = pct === 100;
          const totalLessons = row.total_lessons ?? row.lessons_total ?? 0;
          const completed = row.completed_lessons ?? row.lessons_completed ?? 0;
          return (
            <li
              key={row.course_id || row.id}
              className="rounded-lg border border-border/60 bg-muted/30 hover:bg-muted transition-colors p-3"
              data-testid={`continue-course-${row.course_id || row.id}`}
            >
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-heading text-sm font-bold text-foreground truncate">
                      {row.course_title || row.title || 'Course'}
                    </div>
                    {done && <CompletedPill testid={`continue-completed-${row.course_id || row.id}`} />}
                  </div>
                  <div className="text-[11px] font-semibold text-muted-foreground mt-0.5">
                    {done
                      ? 'You finished this course'
                      : `${completed} of ${totalLessons} lessons · ${pct}%`}
                  </div>
                </div>
                <Button
                  size="sm"
                  onClick={() => resume(row)}
                  className={`rounded-full font-bold h-8 px-3 ${done ? 'bg-emerald-500 hover:bg-emerald-600 text-white' : 'bg-primary hover:bg-primary/90'}`}
                  data-testid={`continue-resume-${row.course_id || row.id}`}
                >
                  <PlayCircle className="w-3.5 h-3.5 mr-1" />
                  {done ? 'Review' : 'Resume'}
                </Button>
              </div>
              <ProgressBar pct={pct} testid={`continue-bar-${row.course_id || row.id}`} />
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default ContinueLearningCard;
